import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, Plus, Trash2, Edit, ArrowLeft, UserPlus, 
  FolderOpen, CheckCircle, Clock, BarChart3, FileText,
  ChevronDown, ChevronUp, User, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TeamManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAppProjects, setShowAppProjects] = useState(false);
  const [showImplProjects, setShowImplProjects] = useState(false);
  
  // Filter states for project status
  const [appProjectFilter, setAppProjectFilter] = useState('active'); // 'active' or 'completed'
  const [implProjectFilter, setImplProjectFilter] = useState('active'); // 'active' or 'completed'
  
  // Filter states for templates
  const [appTemplateFilter, setAppTemplateFilter] = useState('all');
  const [implTemplateFilter, setImplTemplateFilter] = useState('all');
  const [appTemplates, setAppTemplates] = useState([]);
  const [implTemplates, setImplTemplates] = useState([]);
  
  const [newMember, setNewMember] = useState({
    email: '',
    password: '',
    full_name: '',
    ai_check_enabled: true
  });

  useEffect(() => {
    fetchData();
    fetchTemplates();
  }, [user.user_id]);

  const fetchData = async () => {
    try {
      const [membersRes, reportsRes] = await Promise.all([
        axios.get(`${API}/team/members?user_id=${user.user_id}`),
        axios.get(`${API}/team/reports?user_id=${user.user_id}`)
      ]);
      setMembers(membersRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const [appRes, implRes] = await Promise.all([
        axios.get(`${API}/application-templates?user_id=${user.user_id}`),
        axios.get(`${API}/implementation-templates?user_id=${user.user_id}`)
      ]);
      setAppTemplates(appRes.data || []);
      setImplTemplates(implRes.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/team/members?user_id=${user.user_id}`, newMember);
      toast.success('Ο υπάλληλος προστέθηκε');
      setNewMember({ email: '', password: '', full_name: '', ai_check_enabled: true });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα δημιουργίας');
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/team/members/${selectedMember.user_id}?user_id=${user.user_id}`,
        selectedMember
      );
      toast.success('Ο υπάλληλος ενημερώθηκε');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε αυτόν τον υπάλληλο;')) return;
    try {
      await axios.delete(`${API}/team/members/${memberId}?user_id=${user.user_id}`);
      toast.success('Ο υπάλληλος διαγράφηκε');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 20) return 'bg-amber-500';
    return 'bg-slate-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/select-module')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Πίσω
            </Button>
            <h1 className="text-xl font-bold text-slate-900">Διαχείριση Ομάδας & Αναφορές</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            Αποσύνδεση
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Reports Section */}
        {reports && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Συνοπτική Αναφορά
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="text-3xl font-bold text-blue-600">{reports.summary.total_app_projects}</div>
                <div className="text-sm text-slate-600">Έργα Αίτησης</div>
                <div className="text-xs text-slate-400 mt-1">
                  {reports.summary.app_in_progress} σε εξέλιξη, {reports.summary.app_completed} ολοκληρωμένα
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="text-3xl font-bold text-emerald-600">{reports.summary.total_impl_projects}</div>
                <div className="text-sm text-slate-600">Έργα Υλοποίησης</div>
                <div className="text-xs text-slate-400 mt-1">
                  {reports.summary.impl_in_progress} σε εξέλιξη, {reports.summary.impl_completed} ολοκληρωμένα
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="text-3xl font-bold text-purple-600">{reports.summary.total_team_members}</div>
                <div className="text-sm text-slate-600">Μέλη Ομάδας</div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="text-3xl font-bold text-amber-600">
                  {reports.summary.unassigned_app + reports.summary.unassigned_impl}
                </div>
                <div className="text-sm text-slate-600">Μη Ανατεθειμένα</div>
              </div>
            </div>

            {/* Application Projects Details */}
            {reports.app_projects && reports.app_projects.length > 0 && (
              <div className="mt-6 bg-white rounded-lg border border-slate-200">
                <button
                  onClick={() => setShowAppProjects(!showAppProjects)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-slate-900">
                      Αναλυτικά Έργα Αίτησης ({reports.app_projects.length})
                    </span>
                  </div>
                  {showAppProjects ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {showAppProjects && (
                  <div className="border-t border-slate-200">
                    {/* Filter Tabs */}
                    <div className="px-4 py-3 bg-slate-50 border-b flex flex-wrap items-center gap-4">
                      <span className="text-sm text-slate-600">Κατάσταση:</span>
                      <div className="flex bg-white border rounded-lg p-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setAppProjectFilter('active'); }}
                          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            appProjectFilter === 'active' 
                              ? 'bg-blue-600 text-white' 
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Σε Εξέλιξη ({reports.app_projects.filter(p => p.status === 'in_progress').length})
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setAppProjectFilter('completed'); }}
                          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            appProjectFilter === 'completed' 
                              ? 'bg-blue-600 text-white' 
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Ολοκληρωμένα ({reports.app_projects.filter(p => p.status !== 'in_progress').length})
                        </button>
                      </div>
                      
                      {/* Template Filter */}
                      <span className="text-sm text-slate-600 ml-4">Πρότυπο:</span>
                      <select
                        value={appTemplateFilter}
                        onChange={(e) => { e.stopPropagation(); setAppTemplateFilter(e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                      >
                        <option value="all">Όλα τα πρότυπα</option>
                        {appTemplates.map(t => (
                          <option key={t.template_id} value={t.template_id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Έργο</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Πελάτης</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Προθεσμία</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Υπεύθυνος</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Δικαιολογητικά</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Πρόοδος</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Κατάσταση</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {reports.app_projects
                          .filter(p => appProjectFilter === 'active' ? p.status === 'in_progress' : p.status !== 'in_progress')
                          .filter(p => appTemplateFilter === 'all' || p.template_id === appTemplateFilter)
                          .map((project) => (
                          <tr 
                            key={project.project_id} 
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/projects/${project.project_id}`)}
                          >
                            <td className="px-4 py-3 font-medium text-slate-900">{project.project_title}</td>
                            <td className="px-4 py-3 text-slate-600">{project.client_name}</td>
                            <td className="px-4 py-3">
                              {project.deadline ? (
                                <span className="flex items-center gap-1 text-sm text-orange-600">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(project.deadline).toLocaleDateString('el-GR')}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {project.assigned_to_names && project.assigned_to_names.length > 0 ? (
                                <span className="flex items-center gap-1 text-sm">
                                  <User className="h-3 w-3" />
                                  {project.assigned_to_names.join(', ')}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {project.uploaded_documents}/{project.total_documents}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${getProgressColor(project.progress)}`}
                                    style={{ width: `${project.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium">{project.progress}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {project.status === 'completed' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Ολοκληρωμένο
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Σε εξέλιξη
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {reports.app_projects.filter(p => appProjectFilter === 'active' ? p.status === 'in_progress' : p.status !== 'in_progress').length === 0 && (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                              {appProjectFilter === 'active' ? 'Δεν υπάρχουν έργα σε εξέλιξη' : 'Δεν υπάρχουν ολοκληρωμένα έργα'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Implementation Projects Details */}
            {reports.impl_projects && reports.impl_projects.length > 0 && (
              <div className="mt-4 bg-white rounded-lg border border-slate-200">
                <button
                  onClick={() => setShowImplProjects(!showImplProjects)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-slate-900">
                      Αναλυτικά Έργα Υλοποίησης ({reports.impl_projects.length})
                    </span>
                  </div>
                  {showImplProjects ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {showImplProjects && (
                  <div className="border-t border-slate-200">
                    {/* Filter Tabs */}
                    <div className="px-4 py-3 bg-slate-50 border-b flex flex-wrap items-center gap-4">
                      <span className="text-sm text-slate-600">Κατάσταση:</span>
                      <div className="flex bg-white border rounded-lg p-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setImplProjectFilter('active'); }}
                          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            implProjectFilter === 'active' 
                              ? 'bg-purple-600 text-white' 
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Σε Εξέλιξη ({reports.impl_projects.filter(p => p.status === 'in_progress').length})
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setImplProjectFilter('completed'); }}
                          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            implProjectFilter === 'completed' 
                              ? 'bg-purple-600 text-white' 
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Ολοκληρωμένα ({reports.impl_projects.filter(p => p.status !== 'in_progress').length})
                        </button>
                      </div>
                      
                      {/* Template Filter */}
                      <span className="text-sm text-slate-600 ml-4">Πρότυπο:</span>
                      <select
                        value={implTemplateFilter}
                        onChange={(e) => { e.stopPropagation(); setImplTemplateFilter(e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                      >
                        <option value="all">Όλα τα πρότυπα</option>
                        {implTemplates.map(t => (
                          <option key={t.template_id} value={t.template_id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Έργο</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Πελάτης</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Προθεσμία</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Υπεύθυνος</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Δικαιολογητικά</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Πρόοδος</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Κατάσταση</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {reports.impl_projects
                          .filter(p => implProjectFilter === 'active' ? p.status === 'in_progress' : p.status !== 'in_progress')
                          .filter(p => implTemplateFilter === 'all' || p.template_id === implTemplateFilter)
                          .map((project) => (
                          <tr 
                            key={project.project_id} 
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/implementation/projects/${project.project_id}`)}
                          >
                            <td className="px-4 py-3 font-medium text-slate-900">{project.project_title}</td>
                            <td className="px-4 py-3 text-slate-600">{project.client_name}</td>
                            <td className="px-4 py-3">
                              {project.deadline ? (
                                <span className="flex items-center gap-1 text-sm text-orange-600">
                                  <Calendar className="h-3 w-3" />
                                  {new Date(project.deadline).toLocaleDateString('el-GR')}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {project.assigned_to_names && project.assigned_to_names.length > 0 ? (
                                <span className="flex items-center gap-1 text-sm">
                                  <User className="h-3 w-3" />
                                  {project.assigned_to_names.join(', ')}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {project.uploaded_documents}/{project.total_documents}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${getProgressColor(project.progress)}`}
                                    style={{ width: `${project.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium">{project.progress}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {project.status === 'completed' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Ολοκληρωμένο
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Σε εξέλιξη
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {reports.impl_projects.filter(p => implProjectFilter === 'active' ? p.status === 'in_progress' : p.status !== 'in_progress').length === 0 && (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                              {implProjectFilter === 'active' ? 'Δεν υπάρχουν έργα σε εξέλιξη' : 'Δεν υπάρχουν ολοκληρωμένα έργα'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Member Stats */}
            {reports.member_stats.length > 0 && (
              <div className="mt-6 bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="font-medium text-slate-900 mb-3">Έργα ανά Υπάλληλο</h3>
                <div className="space-y-2">
                  {reports.member_stats.map((stat) => (
                    <div key={stat.user_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <button
                          onClick={() => navigate(`/team/statistics/${stat.user_id}`)}
                          className="font-medium text-cyan-600 hover:text-cyan-700 hover:underline transition-colors"
                          data-testid={`member-stats-link-${stat.user_id}`}
                        >
                          {stat.full_name}
                        </button>
                        <span className="text-sm text-slate-500 ml-2">({stat.email})</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-blue-600">{stat.app_projects} αιτήσεις</span>
                        <span className="text-emerald-600">{stat.impl_projects} υλοποιήσεις</span>
                        <span className="font-medium">{stat.total_projects} συνολικά</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Team Members Section */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Υπάλληλοι ({members.length})
            </h2>
            <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
              <UserPlus className="h-4 w-4 mr-2" />
              Προσθήκη Υπαλλήλου
            </Button>
          </div>

          {members.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Δεν έχετε προσθέσει υπαλλήλους ακόμα</p>
              <p className="text-sm mt-1">Προσθέστε υπαλλήλους για να αναθέσετε έργα</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {members.map((member) => (
                <div key={member.user_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-slate-600 font-medium">
                        {(member.full_name || member.email).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">
                        {member.full_name || 'Χωρίς όνομα'}
                      </div>
                      <div className="text-sm text-slate-500">{member.email}</div>
                    </div>
                    {member.ai_check_enabled !== false && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700" data-testid={`ai-check-badge-${member.user_id}`}>
                        AI Έλεγχος
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedMember({ ...member, password: '' });
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-600 hover:text-rose-700"
                      onClick={() => handleDeleteMember(member.user_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Προσθήκη Υπαλλήλου</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMember} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Ονοματεπώνυμο</Label>
              <Input
                id="full_name"
                value={newMember.full_name}
                onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                placeholder="π.χ. Γιώργος Παπαδόπουλος"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                placeholder="employee@company.gr"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Κωδικός</Label>
              <Input
                id="password"
                type="password"
                value={newMember.password}
                onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                placeholder="Κωδικός πρόσβασης"
                required
              />
            </div>
            <div className="flex items-center justify-between py-2 px-1">
              <div>
                <Label htmlFor="ai_check_toggle" className="text-sm font-medium">Αυτόματος Έλεγχος Δικαιολογητικών</Label>
                <p className="text-xs text-slate-500 mt-0.5">Ενεργοποίηση AI ελέγχου αρχείων αίτησης</p>
              </div>
              <button
                type="button"
                id="ai_check_toggle"
                data-testid="create-ai-check-toggle"
                onClick={() => setNewMember({ ...newMember, ai_check_enabled: !newMember.ai_check_enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  newMember.ai_check_enabled ? 'bg-blue-600' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  newMember.ai_check_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Ακύρωση
              </Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                Προσθήκη
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Επεξεργασία Υπαλλήλου</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <form onSubmit={handleUpdateMember} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_full_name">Ονοματεπώνυμο</Label>
                <Input
                  id="edit_full_name"
                  value={selectedMember.full_name || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, full_name: e.target.value })}
                  placeholder="Ονοματεπώνυμο"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={selectedMember.email}
                  onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_password">Νέος Κωδικός (προαιρετικό)</Label>
                <Input
                  id="edit_password"
                  type="password"
                  value={selectedMember.password || ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, password: e.target.value })}
                  placeholder="Αφήστε κενό για να μην αλλάξει"
                />
              </div>
              <div className="flex items-center justify-between py-2 px-1">
                <div>
                  <Label htmlFor="edit_ai_check_toggle" className="text-sm font-medium">Αυτόματος Έλεγχος Δικαιολογητικών</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Ενεργοποίηση AI ελέγχου αρχείων αίτησης</p>
                </div>
                <button
                  type="button"
                  id="edit_ai_check_toggle"
                  data-testid="edit-ai-check-toggle"
                  onClick={() => setSelectedMember({ ...selectedMember, ai_check_enabled: !selectedMember.ai_check_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    selectedMember.ai_check_enabled !== false ? 'bg-blue-600' : 'bg-slate-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedMember.ai_check_enabled !== false ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                  Ακύρωση
                </Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                  Αποθήκευση
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
